export enum LaunchDarklyFeatureFlag {
  // Client-Side Flags - Accessed via useFlags() hook
  DOT_FOOD_WASTE_SECONDARY_SALES = 'dot-food-waste-secondary-sales',
  DOT_FUNDRAISING_SURCHARGING = 'dot-fundraising-surcharging',
  NV1_TOAST_APP_FREE_DELIVERY_ORDERING = 'nv1-toast-app-free-delivery-ordering',
  NVS_CATERING_DISCOVERY_TOAST_LOCAL = 'nvs-catering-discovery-toast-local',
  NVS_CATERING_TOAST_LOCAL = 'nvs-catering-toast-local',
  NVS_MARKETPLACE_LEAD_FORMS = 'nvs-marketplace-lead-forms',
  OO_ADS_PIXEL_CLIENT_RENDER = 'oo-ads-pixel-client-render',
  OO_ALERT_CLOSING_SOON = 'oo-alert-closing-soon',
  OO_CONVO_ORDERING_ENABLED = 'oo-convo-ordering-enabled',
  OO_COOKIE_STORAGE_CART_GUID = 'oo-cookie-storage-cart-guid',
  OO_ECOMM_FILTER_AND_SORT = 'oo-ecomm-filter-and-sort',
  OO_ECOMM_ITEMS_PER_PAGE = 'oo-ecomm-items-per-page',
  OO_ENABLE_MENU_CHANGE_LOCATION = 'oo-enable-menu-change-location',
  OO_REQUIRE_LOCATION_SELECTION = 'oo-require-location-selection',
  OO_ETHYCA_ADS = 'oo-ethyca-ads',
  OO_GENERATE_RECAPTCHA_TOKEN_FOR_INTENT = 'oo-generate-recaptcha-token-for-intent',
  OO_GENERATE_RECAPTCHA_TOKEN_FOR_PAYMENT_KILL_SWITCH = 'oo-generate-recaptcha-token-for-payment-kill-switch',
  OO_GTM_CONSENT_DEFAULT_DENY = 'oo-gtm-consent-default-deny',
  OO_IMAGE_ASPECT_RATIO = 'oo-image-aspect-ratio',
  OO_IMAGE_ASPECT_RATIO_THRESHOLD = 'oo-image-aspect-ratio-threshold',
  OO_INTL_PAYMENTS_IFRAME = 'oo-intl-payments-iframe',
  OO_MULTI_IMAGE_GALLERY = 'oo-multi-image-gallery',
  OO_LOCALE_SELECTOR = 'oo-locale-selector',
  OO_MENU_CACHE_FIRST = 'oo-menu-cache-first',
  OO_ORDER_SUCCESS_PAGE = 'oo-order-success-page',
  OO_RESERVATION_WIDGET_V2 = 'oo-reusable-flag-alpha',
  OO_REUSABLE_FLAG_GOLF = 'oo-reusable-flag-golf',
  OO_SEARCH_PAGE_SIZE = 'oo-search-page-size',
  OO_ACCEPT_IMPORTED_GIFTCARDS = 'oo-accept-imported-giftcards',
  OO_SERVER_SPI = 'oo-server-spi',
  OO_SPI_SURCHARGING_FE = 'oo-spi-surcharging-fe',
  OO_SPI_TEMP_EVENTS_ENABLED = 'oo-spi-temp-events-enabled',
  OO_TOAST_CASH_SPEND = 'oo-toast-cash-spend',
  OO_TRACKING_DISABLED = 'oo-tracking-disabled',
  OO_ENTP_FREEDOMPAY_ECOMM_ENABLED = 'oo-entp-freedompay-ecomm-enabled',
  STP_TOAST_CASH_WALLET_WEB_ENABLED = 'stp-toast-cash-wallet-web-enabled',
  INTL_ADDRESS_COUNTRY_LOGIC_ERROR_HANDLING = 'intl-address-country-logic-error-handling',
  // Environment-level kill switch for the Next.js app. Distinct from the
  // per-page flags below: this only answers "is Next.js serving anything in this
  // environment?". Read on the client so link handling knows not to treat any
  // route as Next.js-owned while the app is switched off.
  OO_SITES_NEXTJS_ROUTER = 'oo-sites-nextjs-router',
  OO_SITES_NEXTJS_ACCOUNT_PAGE = 'oo-sites-nextjs-account-page',
  OO_SITES_NEXTJS_CONFIRM_PAGE = 'oo-sites-nextjs-confirm-page',
  OO_SITES_NEXTJS_FORM_PAGE = 'oo-sites-nextjs-form-page',
  OO_SITES_NEXTJS_FILE_PAGE = 'oo-sites-nextjs-file-page',
  OO_SITES_NEXTJS_GIFT_CARD_PURCHASE_PAGE = 'oo-sites-nextjs-gift-card-purchase-page',
  OO_SITES_NEXTJS_LOYALTY_PAGE = 'oo-sites-nextjs-loyalty-page',
  OO_ASK_MAPS_CASH_ENABLED = 'oo-ask-maps-cash-enabled',
  OO_SITES_NEXTJS_IFRAME_PAGE = 'oo-sites-nextjs-iframe-page',
  OO_BOOST_OFFERS = 'oo-boost-offers',
  OO_INTL_USE_TOASTTAB_CHECKOUT = 'oo-intl-use-toasttab-checkout',
  // Boolean gate for the entire Revere "Get help" entry point (does the card render).
  TDS_REVERE_WEB_INTERCOM = 'tds-revere-web-intercom',

  // Server-Side Flags - Accessed via Express Request object (req.flags) in server middleware
  CA_TOAST_LOCAL_WEB_APPLE_BANNER = 'ca-toast-local-web-apple-banner',
  INTL_OO_COOKIE_BANNER = 'intl-oo-cookie-banner',
  NV1_TOAST_APP_LOCAL_RESTAURANT_REDIRECT = 'nv1-toast-app-local-restaurant-redirect',
  NV5_SITES_WEB_CACHING_ENABLED = 'nv5-sites-web-caching-enabled',
  NV5_SITES_WEB_CACHE_TTL = 'nv5-sites-web-cache-ttl',
  OO_GTM_CONTENT_SECURITY_POLICY = 'oo-gtm-content-security-policy',
  OPOP_CASH_ROUNDING_DISCLAIMER = 'opop-cash-rounding-disclaimer'
}

/**
 * Helper to get all flag keys
 * Returns the same singleton instance on every call for optimal performance
 */
const flagKeys = Object.values(LaunchDarklyFeatureFlag)
export const getAllFeatureFlagKeys = (): string[] => flagKeys

/**
 * Utility type to convert kebab-case to camelCase
 * e.g., 'oo-new-feature' -> 'ooNewFeature'
 */
type KebabToCamelCase<S extends string> = S extends `${infer T}-${infer U}`
  ? `${Lowercase<T>}${Capitalize<KebabToCamelCase<U>>}`
  : Lowercase<S>

// Map of flags that return non-boolean values and are not mapped to an A/B test
type FlagValueTypeOverrides = {
  // Configuration flags that return numbers
  'nv5-sites-web-cache-ttl': number
  'oo-ecomm-items-per-page': number
  'oo-search-page-size': number
  // Configuration flags that return strings
  'oo-image-aspect-ratio-threshold': string
}

// Helper type to get the value type for a specific flag
type FlagValueType<K extends string> = K extends keyof FlagValueTypeOverrides
  ? FlagValueTypeOverrides[K]
  : boolean

/**
 * Type representing all LaunchDarkly flags in camelCase format
 * This is how they're accessed via useFlags() hook after LaunchDarkly converts them
 *
 * Most flags return boolean, but specific flags can be marked with their actual type
 * in the FlagValueTypeOverrides map above.
 */
export type LaunchDarklyFlags = {
  [K in LaunchDarklyFeatureFlag as KebabToCamelCase<K>]: FlagValueType<K>
}

export type LaunchDarklyFlagsKebab = {
  [K in LaunchDarklyFeatureFlag]: FlagValueType<K>
}
